<template>
  <div class="flex flex-col gap-4 text-left" data-testid="pin-step-1">
    <div class="w-full sm:max-w-[280px]">
      <v-select
        label="* Tipo de unidade"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        :items="HEALTH_UNIT_TYPE_OPTIONS"
        :model-value="geo.unitType.value"
        @update:model-value="geo.setUnitType($event)"
        data-testid="pin-type-select"
      />
    </div>

    <!--
      Na edição o pino já existe: mostrar onde ele está explica por que o
      "Continuar" nasce habilitado sem nenhuma busca feita.
    -->
    <div
      v-if="currentPointLabel"
      class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white p-3"
      data-testid="pin-current-position"
    >
      <LabelBadge size="small" :icon="null" color="blue" label="Posição atual" />
      <span class="font-mono text-xs text-gray-600">{{ currentPointLabel }}</span>
    </div>

    <div class="rounded-xl border border-gray-100 bg-white p-4">
      <div class="flex items-end gap-2">
        <div class="min-w-0 flex-1">
          <GenericInput
            label="Buscar estabelecimento no Google"
            :model-value="query"
            @update:model-value="query = $event"
            @keyup.enter="runSearch()"
            width="100%"
            placeholder="Ex: UBS Darlene Fernandes Capim Grosso"
            hide-details
            data-testid="pin-search-input"
          />
        </div>
        <FilledButton
          class="mb-1 flex-none"
          text="Buscar"
          width="110px"
          height="56px"
          :prepend-icon="mdiMagnify"
          :disabled="!query.trim()"
          :loading="isSearching"
          @click="runSearch()"
          data-testid="pin-search-button"
        />
      </div>

      <p class="mt-2 text-xs text-gray-500">
        Busque pelo nome como aparece no Google Maps. O resultado traz o ponto do prédio, não do
        endereço.
      </p>

      <p v-if="searchError" class="mt-2 text-sm text-negative" data-testid="pin-search-error">
        {{ searchError }}
      </p>

      <ul v-if="candidates.length" class="mt-3 flex flex-col gap-2">
        <li v-for="(candidate, index) in candidates" :key="candidate.id">
          <button
            type="button"
            class="w-full rounded-lg border p-3 text-left transition hover:border-[#2F7F91]"
            :class="
              candidate.id === selectedId
                ? 'border-[#2F7F91] bg-primary5'
                : 'border-gray-100 bg-white'
            "
            @click="select(candidate)"
            @dblclick="emit('next')"
            :data-testid="`pin-candidate-${index}`"
          >
            <p class="text-sm font-semibold text-gray-800">{{ candidate.name }}</p>
            <p class="text-xs text-gray-500">{{ candidate.address }}</p>
            <LabelBadge
              class="mt-2"
              size="small"
              :icon="null"
              :color="candidate.confidence === 'BUILDING' ? 'green' : 'yellow'"
              :label="confidenceLabel(candidate.confidence)"
            />
          </button>
        </li>
      </ul>

      <p
        v-else-if="hasSearched && !isSearching && !searchError"
        class="mt-3 text-sm text-gray-500"
        data-testid="pin-search-empty"
      >
        Nenhum estabelecimento encontrado para essa busca.
      </p>
    </div>

    <div class="rounded-xl border border-gray-100 bg-white p-4">
      <p class="text-sm font-semibold text-gray-800">Não achou?</p>

      <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <OutlinedButton
          text="Usar o endereço do cadastro"
          width="auto"
          height="56px"
          class="!w-auto !px-4 text-sm"
          :disabled="!registeredAddress || isGeocoding"
          :loading="isGeocoding"
          @click="useRegisteredAddress()"
          data-testid="pin-use-address-button"
        />

        <div class="flex min-w-0 flex-1 items-end gap-2">
          <div class="min-w-0 flex-1">
            <GenericInput
              label="Colar link do Google Maps"
              :model-value="geo.googleMapsLink.value"
              @update:model-value="geo.setGoogleMapsLink($event)"
              width="100%"
              placeholder="Ex: https://maps.app.goo.gl/..."
              hide-details
              data-testid="pin-maps-link-input"
            />
          </div>
          <FilledButton
            class="flex-none"
            text="Ir"
            width="80px"
            height="56px"
            :disabled="!geo.googleMapsLink.value"
            :loading="geo.isLoadingLink.value"
            @click="geo.applyMapsLink()"
            data-testid="pin-apply-link-button"
          />
        </div>
      </div>
    </div>

    <p class="text-xs text-gray-500" data-testid="pin-registered-address">
      Endereço do cadastro: {{ registeredAddress || '—' }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiMagnify } from '@mdi/js'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage } from '@/utils/apiError'
import type { PinConfidence, PlaceCandidate } from '@/Network/Services/PlacesService'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { HEALTH_UNIT_TYPE_OPTIONS } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'

const props = defineProps<{
  geo: UnitGeolocationApi
  /** Endereço textual já cadastrado na unidade — o plano B quando o Places falha. */
  registeredAddress: string
}>()

/** O caminho principal é o "Continuar" do rodapé; o duplo clique é só um atalho. */
const emit = defineEmits<{ (e: 'next'): void }>()

const { notifyErrorMessage } = useErrorToast()

const PLACES_SEARCH_FALLBACK =
  'Não foi possível buscar o estabelecimento. Use o endereço do cadastro ou cole o link do Google Maps.'

const query = ref('')
const candidates = ref<PlaceCandidate[]>([])
const selectedId = ref<string | null>(null)
const isSearching = ref(false)
const isGeocoding = ref(false)
const hasSearched = ref(false)
const searchError = ref('')

/** Só enquanto nenhum candidato foi buscado: depois disso o destaque é o selecionado. */
const currentPointLabel = computed(() => {
  const point = props.geo.currentPoint.value
  if (!point || !props.geo.foundLocation.value || candidates.value.length) return ''
  return `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`
})

function confidenceLabel(confidence: PinConfidence): string {
  return confidence === 'BUILDING' ? 'Prédio identificado' : 'Endereço aproximado'
}

async function runSearch(): Promise<void> {
  const text = query.value.trim()
  if (!text) return

  isSearching.value = true
  searchError.value = ''
  try {
    // Enviesa pelo ponto atual quando já existe um: unidade em edição costuma
    // estar na cidade certa mesmo com o pino no lugar errado.
    candidates.value = await Network.places.searchText(
      text,
      props.geo.currentPoint.value ?? undefined
    )
    selectedId.value = null
  } catch (e) {
    // Funil canônico: a mensagem do service (que já aponta os planos B) ou o
    // fallback — nunca o texto técnico de um erro cru.
    const message = getApiErrorMessage(e, PLACES_SEARCH_FALLBACK)
    // Inline e no toast: o erro do Places manda o usuário para os planos B logo abaixo.
    searchError.value = message
    notifyErrorMessage(message)
    candidates.value = []
  } finally {
    hasSearched.value = true
    isSearching.value = false
  }
}

/** O geocode do endereço do cadastro é uma ida à rede — o botão precisa mostrar isso. */
async function useRegisteredAddress(): Promise<void> {
  if (!props.registeredAddress) return

  isGeocoding.value = true
  try {
    await props.geo.useRegisteredAddress(props.registeredAddress)
  } finally {
    isGeocoding.value = false
  }
}

function select(candidate: PlaceCandidate): void {
  selectedId.value = candidate.id
  props.geo.setFromPlace(candidate)
}
</script>
